// @ts-check
import { WHATSAPP_MESSAGE_STATUS, formatPhoneDisplay, skipReasonLabel, statusLabel } from '../../utils/whatsapp';
import MessageTicks from './MessageTicks';

/** The backend reports rows a cancel left in the queue as display_status "not_sent". */
const NOT_SENT = 'not_sent';

/**
 * Per-recipient table for one campaign, with a status filter and simple previous/next paging. Purely
 * presentational: the parent owns the data and reloads on onFilterChange / onPageChange.
 * @param {{
 *   page: import('../../services/adminWhatsAppService').RecipientsPage,
 *   statusFilter: string,
 *   onFilterChange: (status: string) => void,
 *   onPageChange: (page: number) => void,
 *   busy?: boolean,
 * }} props
 */
const RecipientsTable = ({ page, statusFilter, onFilterChange, onPageChange, busy = false }) => {
  const { items, total, page: current, pages } = page;
  return (
    <section aria-label="Recipients" className="bg-card border border-border rounded-lg">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
        <h3 className="text-base font-medium text-foreground">
          Recipients <span className="text-muted-foreground">({total})</span>
        </h3>
        <select
          aria-label="Filter recipients by status"
          value={statusFilter}
          onChange={(e) => onFilterChange(e.target.value)}
          className="px-3 py-2 text-base border border-input rounded-lg bg-card"
        >
          <option value="">All statuses</option>
          {Object.values(WHATSAPP_MESSAGE_STATUS).map((status) => (
            <option key={status} value={status}>
              {statusLabel(status)}
            </option>
          ))}
        </select>
      </div>

      {items.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground text-center">No recipients match this filter.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Phone</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Skipped because</th>
                <th className="px-4 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const status = row.display_status || row.status;
                return (
                  <tr key={row.id} className="border-b border-border/60 last:border-0 align-top">
                    <td className="px-4 py-2 text-foreground">{row.name || '-'}</td>
                    <td className="px-4 py-2 text-foreground/80 whitespace-nowrap">{formatPhoneDisplay(row.phone)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        {status !== NOT_SENT && <MessageTicks status={status} />}
                        <span className="text-foreground/80">{statusLabel(status)}</span>
                      </span>
                    </td>
                    <td className="px-4 py-2 text-foreground/80">
                      {row.skip_reason ? skipReasonLabel(row.skip_reason) : ''}
                    </td>
                    <td className="px-4 py-2 text-red-700 dark:text-red-300 break-words min-w-[12rem]">
                      {row.error || ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border">
          <button
            type="button"
            onClick={() => onPageChange(current - 1)}
            disabled={busy || current <= 1}
            className="px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {current} of {pages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(current + 1)}
            disabled={busy || current >= pages}
            className="px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
};

export default RecipientsTable;
